/**
 * ModulesNavDropdown — the "Modules" nav item with a hover/focus mega-menu
 * listing every live module (icon + name + epithet), each linking to its
 * detail page. Data comes from the live catalog (`useProducts`), filtered to
 * non-archived rows in wire (sort_order) order — the same source the
 * /modules page uses, so the menu never drifts from the catalog.
 *
 * Pure CSS reveal via `group-hover` + `group-focus-within` (keyboard-
 * reachable). The trigger itself links to /modules, so a click still works
 * if the dropdown is ignored. Token-only colors per styling-standards.
 */
import { ChevronDown } from 'lucide-react'
import { Link } from 'react-router-dom'

import { useProducts } from '@/lib/store'
import { splitModuleTitle } from '@/core/marketing/components/modules/splitModuleTitle'

export default function ModulesNavDropdown() {
  const { data } = useProducts()
  const modules = (data ?? []).filter((p) => !p.archived)

  return (
    <div className="group relative">
      <Link
        to="/modules"
        className="nav-link inline-flex items-center gap-1"
        aria-haspopup="true"
      >
        Modules
        <ChevronDown
          className="h-3.5 w-3.5 transition-transform duration-200 group-hover:rotate-180"
          aria-hidden
        />
      </Link>

      {/* Panel — invisible until hover/focus; pt-3 bridges the gap so the
          pointer doesn't fall through between trigger and panel. */}
      <div className="invisible absolute left-0 top-full z-50 pt-3 opacity-0 transition-opacity duration-200 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
        <div className="w-[34rem] max-w-[90vw] rounded-lg border border-[var(--color-dark-4)] bg-dark-2 p-2 shadow-[0_10px_40px_0_rgba(0,0,0,0.7)]">
          {modules.length === 0 ? (
            <p className="px-3 py-4 text-sm text-muted-godlike">
              Loading modules…
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-1">
              {modules.map((m) => {
                const { title, tagline } = splitModuleTitle(m.name, m.tagline)
                return (
                  <Link
                    key={m.slug}
                    to={`/modules/${m.slug}`}
                    className="flex items-center gap-3 rounded-md px-2 py-2 transition-colors hover:bg-[var(--color-dark-4)]"
                  >
                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center overflow-hidden rounded bg-[var(--color-main-1)]/10">
                      <img
                        src={m.logoUrl ?? '/Logos/placeholder.webp'}
                        alt=""
                        aria-hidden
                        className="h-full w-full object-contain"
                        loading="lazy"
                      />
                    </div>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-white">
                        {title}
                      </div>
                      {tagline ? (
                        <div className="truncate text-xs text-muted-godlike">
                          {tagline}
                        </div>
                      ) : null}
                    </div>
                  </Link>
                )
              })}
            </div>
          )}
          <Link
            to="/modules"
            className="mt-1 block border-t border-[var(--color-dark-4)] px-3 pt-2 pb-1 text-sm font-medium text-accent-godlike hover:underline"
          >
            View all modules →
          </Link>
        </div>
      </div>
    </div>
  )
}
